import useCreditApplications from "hooks/donations/useCreditApplications";
import useDonationsPurposes from "hooks/donations/useDonationPurposes";
import useDonationsList from "hooks/donations/useDonationsList";
import useFormattedDate from "hooks/useFormatDate";
import React, { useEffect, useState } from "react";
import { Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import AsyncSelect from "react-select/async";
import selectStyles, { multiSelectStyles } from "../credits/selectStyles";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import SubtleBadge from "components/common/SubtleBadge";
import { getRandomColor } from "helpers/utils";

export const IssueApplicationCredit = () => {
  const { applicationId } = useParams();
  const navigate = useNavigate();

  const {
    loading,
    fetchCreditApplicationById,
    issueApplicationCreditAction,
    error,
  } = useCreditApplications();
  const { donations: allDonations, loading: donationsLoading } =
    useDonationsList();
  const { donationPurposes: allPurposes, loading: purposesLoading } =
    useDonationsPurposes();
  const { formatDateTime } = useFormattedDate();
  const [creditApp, setCreditApp] = useState(null);

  // Form fields
  const [selectedDonation, setSelectedDonation] = useState(null);
  const [amount, setAmount] = useState(0);
  const [expiresAt, setExpiresAt] = useState("");
  const [selectedPurposes, setSelectedPurposes] = useState([]);
  const [notes, setNotes] = useState("");

  const [amountError, setAmountError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchCreditApp = async () => {
      if (!applicationId) return;
      try {
        const application = await fetchCreditApplicationById(applicationId);
        setCreditApp(application);

        // Preselect purposes belonging to this donation
        if (application?.purposeIds?.length) {
          const appPurposes = allPurposes?.filter((purpose) =>
            application?.purposeIds.includes(purpose.id)
          );
          setSelectedPurposes(
            appPurposes?.map((p) => ({
              value: p.id,
              label: p.name,
            }))
          );
        }
      } catch (err) {
        console.error("Error fetching donation:", err);
        setCreditApp(null);
      }
    };
    fetchCreditApp();
  }, [applicationId, fetchCreditApplicationById, allPurposes, purposesLoading]);

  // 1. filter donations -> get donations whose purposes match
  // the credit application purposes
  const filteredDonations = React.useMemo(() => {
    if (!creditApp || !creditApp.purposeIds || !allDonations?.length) return [];

    return allDonations
      .filter(
        (donation) =>
          donation.balance > 0 &&
          donation.status === "ACTIVE" &&
          donation.purposes?.some((p) => creditApp.purposeIds.includes(p.id))
      )
      .sort((a, b) => b.balance - a.balance);
  }, [allDonations, creditApp]);

  useEffect(() => {
    if (filteredDonations.length && !selectedDonation) {
      const firstDonation = filteredDonations[0];
      setSelectedDonation({
        value: firstDonation,
        label: `${firstDonation?.code} - ${firstDonation?.donor?.firstName} ${firstDonation?.donor?.lastName} - $${firstDonation?.balance}`,
      });
    }
  }, [filteredDonations, selectedDonation]);

  // Async loader for donations
  const loadDonations = async (inputValue) => {
    const filtered = filteredDonations.filter((d) =>
      `${d.code} - ${d?.donor?.firstName} ${d?.donor?.lastName} - $${d?.balance}`
        .toLowerCase()
        .includes(inputValue.toLowerCase())
    );

    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(
          filtered.map((d) => ({
            value: d,
            label: `${d?.code} - ${d?.donor?.firstName} ${d?.donor?.lastName} - $${d?.balance}`,
          }))
        );
      }, 1000);
    });
  };

  const statusMap = {
    ACTIVE: { type: "success", label: "Active" },
    PENDING: { type: "warning", label: "Pending" },
    EXPIRED: { type: "danger", label: "Expired" },
    DELETED: { type: "danger", label: "Deleted" },
  };

  const { type, label } = statusMap[selectedDonation?.value.status] || {
    type: "secondary",
    label: "Unknown",
  };

  // Async loader for purposes
  const loadPurposes = async (inputValue) => {
    if (!inputValue || inputValue.trim() === "") {
      // return all purposes when no input
      return allPurposes?.map((p) => ({
        value: p.id,
        label: p.name,
      }));
    }

    // filter based on search input
    const filtered = allPurposes?.filter((p) =>
      p.name.toLowerCase().includes(inputValue.toLowerCase())
    );

    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(
          filtered.map((p) => ({
            value: p.id,
            label: p.name,
          }))
        );
      }, 1000);
    });
  };

  const handleAmountChange = (e) => {
    const val = parseFloat(e.target.value);
    setAmount(val);

    const balance = Number(selectedDonation?.value?.balance || 0);

    if (val > balance) {
      setAmountError(
        `Amount exceeds available balance of $${balance.toFixed(2)}.`
      );
    } else if (val <= 0) {
      setAmountError("Amount must be greater than 0.");
    } else {
      setAmountError("");
    }
  };

  const handleSubmit = async (e) => {
    setSubmitting(true);
    e.preventDefault();

    if (!selectedDonation) {
      toast.error("Please select a donation");
      return;
    }

    if (selectedDonation?.value?.balance <= 0) {
      toast.error("This donation has zero balance");
      return;
    }

    // expiry check
    if (!expiresAt) {
      toast.error("Please select an expiry date.");
      return;
    }

    if (!amount || amount <= 0) {
      toast.error("Enter a valid credit amount");
      return;
    }

    // purposes check (selectedPurposes is AsyncSelect value array)
    if (!selectedPurposes || selectedPurposes.length === 0) {
      toast.error("Select at least one purpose.");
      return;
    }

    // check if status is eligible for credit
    if (creditApp.status === "PENDING") {
      toast.error("Can't issue credit. Application not approved");
      return;
    }

    const payload = {
      notes: notes || `Application approved — issuing ${amount} of credit.`,
      amount: parseFloat(amount),
      userId: creditApp.userId,
      unit: "MONEY",
      currency: "USD",
      expiresAt: new Date(expiresAt).toISOString(),
      status: "ACTIVE",
      donationId: selectedDonation?.value?.id,
      purposes: selectedPurposes.map((p) => p.value),
    };

    try {
      await issueApplicationCreditAction(applicationId, payload);
      toast.success("Credit issued successfully!");
      navigate("/olitask/donations/credits");
    } catch (err) {
      let message = "An unexpected error occurred.";
      if (err.response) {
        message =
          err.response.data?.message ||
          `Failed to issue credit: ${err.response.statusText}`;
      } else if (err.request) {
        message =
          "No response from the server. Please check your internet connection.";
      } else {
        message = err.message || "Something went wrong.";
      }
      toast.error(message);
      console.error("Error issuing credit:", err);
    } finally {
      setSubmitting(false);
    }
  };

  if (donationsLoading || purposesLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spinner animation="border" variant="success" />
        <span className="ms-3" style={{ fontSize: "13.33px" }}>
          Loading...
        </span>
      </div>
    );
  }
  return (
    <>
      <Card className="mb-4 shadow-none mt-3 rounded border-1">
        <Card.Body className="d-flex align-items-center py-3 px-4">
          {/* Left side: Back button */}
          <div className="d-flex align-items-center" style={{ minWidth: 90 }}>
            <Button
              type="button"
              onClick={() =>
                navigate(
                  `/olitask/donations/credit-applications/${applicationId}`
                )
              }
              size="sm"
              aria-label="Go back"
              variant="outline-success"
            >
              <span style={{ fontSize: "1.3em", lineHeight: 1 }}>←</span> Back
            </Button>
          </div>

          {/* Center: Title */}
          <div className="px-3">
            <h4 className="mb-0 fw-semibold text-base">
              Issue Credit from Application
            </h4>
          </div>
        </Card.Body>
      </Card>

      <Row className="mt-3">
        <Col lg={5} xl={4} className="mb-4 mt-4 mt-lg-0 order-2 order-lg-1">
          <Card className="rounded-4 custom-shadow">
            <Card.Header className="pb-1">
              <h6 className="mb-0 fw-bold text-sm">Donation</h6>
            </Card.Header>
            <Card.Body className="py-0 pb-1">
              <Row>
                <Col>
                  <div className="mb-3">
                    <div className="mb-2">
                      <label className="form-label text-xxs mb-0">
                        Donation ID
                      </label>
                      <div className="d-flex align-items-center justify-content-between">
                        <h6 className="mb-0 fw-semibold text-xs">
                          {selectedDonation?.value?.id.slice(0, 25)}....
                        </h6>
                        <Link
                          to={`/olitask/donations/${selectedDonation?.value?.id}`}
                          variant="outline-light"
                          className="text-success"
                        >
                          <FontAwesomeIcon icon="eye" />
                        </Link>
                      </div>
                    </div>
                    <div className="mb-2">
                      <label className="form-label text-xxs mb-0">Donor</label>
                      <h6 className="mb-0 fw-semibold text-xs">
                        {selectedDonation?.value.donor?.firstName}{" "}
                        {selectedDonation?.value.donor?.lastName}
                      </h6>
                    </div>
                    <div className="mb-2">
                      <label className="form-label text-xxs mb-0">Code</label>
                      <h6 className="mb-0 fw-semibold text-xs">
                        {selectedDonation?.value?.code}
                      </h6>
                    </div>

                    <div className="d-flex gap-7 justify-content-betweengg">
                      <div className="mb-2">
                        <label className="form-label text-xxs mb-0">
                          Amount
                        </label>
                        <h6 className="mb-0 fw-semibold text-xs">
                          $
                          {selectedDonation?.value?.amount
                            ? selectedDonation?.value?.amount.toFixed(2)
                            : 0.0}
                        </h6>
                      </div>
                      <div className="mb-2">
                        <label className="form-label text-xxs mb-0">
                          Balance
                        </label>
                        <h6 className="mb-0 fw-bold text-xs text-success">
                          $
                          {selectedDonation?.value?.balance
                            ? selectedDonation?.value?.balance.toFixed(2)
                            : "0.00"}
                        </h6>
                      </div>
                    </div>

                    <div className="mb-2">
                      <label className="form-label text-xxs mb-0">
                        Currency
                      </label>
                      <h6 className="mb-0 fw-semibold text-xs">
                        {selectedDonation?.value?.currency}
                      </h6>
                    </div>
                    <div className="mb-2">
                      <label className="form-label text-xxs mb-0">Note</label>
                      <h6 className="mb-0 fw-semibold text-xs">
                        {selectedDonation?.value?.note || "Not provided"}
                      </h6>
                    </div>
                    <div className="mb-2">
                      <div className="mb-2">
                        <label className="form-label text-xxs mb-0 d-block">
                          Status
                        </label>
                        <SubtleBadge pill bg={type}>
                          {label}
                        </SubtleBadge>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={7} className="order-1 order-lg-2">
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Donation</Form.Label>
              <AsyncSelect
                cacheOptions
                defaultOptions={filteredDonations.map((d) => ({
                  value: d,
                  label: `${d?.code} - ${d?.donor?.firstName} ${d?.donor?.lastName} - $${d?.balance}`,
                }))}
                loadOptions={loadDonations}
                onChange={(opt) => setSelectedDonation(opt)}
                value={selectedDonation}
                placeholder="Search and select donation..."
                isClearable
                isLoading={donationsLoading}
                styles={selectStyles}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Purposes</Form.Label>
              <AsyncSelect
                isMulti
                cacheOptions
                defaultOptions={allPurposes?.map((p) => ({
                  value: p.id,
                  label: p.name,
                }))}
                loadOptions={loadPurposes}
                value={selectedPurposes}
                onChange={(options) => setSelectedPurposes(options)}
                placeholder="Select or search purposes..."
                isLoading={purposesLoading}
                styles={multiSelectStyles}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Amount</Form.Label>
              <Form.Control
                type="number"
                min="0.01"
                step="0.01"
                value={amount}
                onChange={handleAmountChange}
                isInvalid={!!amountError}
                placeholder="0.01"
              />
              <Form.Control.Feedback type="invalid">
                {amountError}
              </Form.Control.Feedback>
              {!amountError && (
                <small>
                  Available balance: $
                  {selectedDonation?.value.balance?.toFixed(2)}
                </small>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Expiry Date</Form.Label>
              <Form.Control
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add a note for the credit..."
                required
              />
            </Form.Group>

            {submitting ? (
              <Spinner animation="border" variant="success" />
            ) : (
              <Button
                type="submit"
                variant="success"
                disabled={selectedDonation?.value?.balance == 0 || !amount}
                className="text-sm"
              >
                Issue Credit
              </Button>
            )}
          </Form>
        </Col>
      </Row>
    </>
  );
};
